/**
 * Load dynamic content using AJAX or Fetch api
 */
import axios from 'axios';
import {isEmpty} from 'lodash';
import LazyLoadingImage from './LazyLoadingImage';
import Overlay from './Overlay';
import CartHint from './CartHint';
import Reminder from './Reminder';
import Quickview from './Quickview';
import AddTo from './AddTo';

class DynamicLoader {

  constructor() {

    this.loaded = false;
    this.elements = {};
    this.elementsInView = {};
    this.idListInView = {};
    this.idList = {};
    this.index = 1;
    this.contentUpdated = false;
    const range = document.createRange();
    this._ccfSupported = ('createContextualFragment' in range);
  }

  /**
   *
   * @param {DOMElement} el
   * @return {boolean}
   */
  isInViewport(el) {
    return true;
  }

  setup() {

    document.querySelectorAll('[data-load]').forEach(el => {

      const contentUri = el.getAttribute('data-load');
      let contentParams = el.getAttribute('data-load-params');
      const contentId = contentUri + ':' + contentParams;
      try {
        contentParams = JSON.parse(contentParams);
      } catch (e) {
        // contentParams was not valid JSON, use as-is
      }

      if (!el.id) {

        el.id = 'dcl-' + this.index;
        this.index++;
      }

      if (this.isInViewport(el)) {
        if (this.idListInView[contentUri]) {
          this.idListInView[contentUri].push(contentParams);
        } else {
          this.idListInView[contentUri] = [contentParams];
        }
        if (this.elementsInView[contentId]) {
          this.elementsInView[contentId].push(el.id);
        } else {
          this.elementsInView[contentId] = [el.id];
        }
      } else {
        if (this.idList[contentUri]) {
          this.idList[contentUri].push(contentParams);
        } else {
          this.idList[contentUri] = [contentParams];
        }
        if (this.elements[contentId]) {
          this.elements[contentId].push(el.id);
        } else {
          this.elements[contentId] = [el.id];
        }
      }
    });

    if (isEmpty(this.idList) && isEmpty(this.idListInView)) {

      return;
    }

    if (document.readyState == 'complete') {

      this.loadContent();
    } else {
      const loadContent = this.loadContent.bind(this);
      window.addEventListener('DOMContentLoaded', loadContent, false);
    }
  }

  loadContent() {

    if (this.loaded) {

      return;
    }

    this.contentUpdated = false;
    const CHUNK_SIZE = 100;
    const requestsInView = [];
    const requests = [];

    Object.keys(this.idListInView).forEach(prop => {

      if (this.idListInView[prop].length < CHUNK_SIZE) {

        requestsInView.push(this.loadContentSlice({[prop]: this.idListInView[prop]}));
      } else {

        this.handleChunks(
          CHUNK_SIZE,
          prop,
          this.idListInView,
          requestsInView
        );
      }
    });

    Object.keys(this.idList).forEach(prop => {

      if (this.idList[prop].length < CHUNK_SIZE) {

        requests.push(this.loadContentSlice({[prop]: this.idList[prop]}));
      } else {

        this.handleChunks(
          CHUNK_SIZE,
          prop,
          this.idList,
          requests
        );
      }
    });

    if (requestsInView.length) {

      axios.all(requestsInView)
        .then(response => {

          response.forEach(res => {

            this.processResponse(res, this.elementsInView);
          });
          Promise.resolve();
        })
        .then(() => {

          this.contentUpdate();
        })
        .catch(error => {
          throw `AjaxForm.js, axios requestsInView fetch failed: ${error.message}`;
        });
    }
    if (requests.length) {

      axios.all(requests)
        .then(response => {

          response.forEach(res => {

            this.processResponse(res, this.elements);
          });
          Promise.resolve();
        })
        .then(() => {

          this.contentUpdate();
        })
        .catch(error => {
          throw `DynamicLoader.js, axios requests fetch failed: ${error.message}`;
        });
    }
  }

  handleChunks(chunk_size, property, arrayList, destination) {

    let i = 0;
    let j = chunk_size;
    let chunk_number = Math.floor(arrayList[property].length / chunk_size);

    while (chunk_number) {

      const CHUNK = {[property]: []};
      for (i; i < j; i++) {

        CHUNK[property].push(arrayList[property][i]);
      }
      i = j;
      j += chunk_size;
      destination.push(this.loadContentSlice(CHUNK));
      chunk_number--;
    }

    if (arrayList[property].length % chunk_size) {

      const CHUNK_REST = {[property]: []};
      for (i; i < arrayList[property].length; i++) {

        CHUNK_REST[property].push(arrayList[property][i]);
      }

      destination.push(this.loadContentSlice(CHUNK_REST));
    }
  }

  contentUpdate() {

    if (this.contentUpdated) {

      this.contentUpdated = false;
      window.responsiveImagesManager.setup();
      window.taikoComponent.reloadModule(LazyLoadingImage, '.tc-lazy-loading-image');
      window.taikoComponent.reloadModule(CartHint, '.tc-add-to-cart');
      window.taikoComponent.reloadModule(Overlay, '.tc-overlay,.tc-quickview-button');
      window.taikoComponent.reloadModule(Quickview, '.tc-quickview');
      window.taikoComponent.reloadModule(Reminder, '.tc-set-reminder');
      window.taikoComponent.reloadModule(AddTo, '.tc-add-to');
    }
  }

  /**
   *
   * @param {string[]} idList
   * @returns {Promise}
   */
  loadContentSlice(idList) {
    return axios.get(window.routes.dynamicLoader[0], {
      headers: {
        'X-Requested-With': 'XMLHttpRequest'
      },
      params: {
        '_': JSON.stringify(idList)
      }
    });
  }

  /**
   * @param {object} response
   * @param {object} elements
   * @returns {undefined}
   */
  processResponse(response, elements) {

    for (let contentId in response.data) {

      const content = response.data[contentId];
      const elementIds = elements[contentId];
      if (elementIds) {

        elementIds.forEach(id => {

          const el = document.getElementById(id);
          if (!el) {

            return;
          }
          if (el.handler && el.handler.onLoadDynamic) {

            el.handler.onLoadDynamic(content);
          } else {

            if (contentId.indexOf('/') >= 0) {

              if (this._ccfSupported) {

                const fragment = document.createRange().createContextualFragment(content);
                const container = el.parentNode;
                container.replaceChild(fragment, el);
              } else {

                el.outerHTML = content;
              }
              this.contentUpdated = true;
            } else {

              el.innerHTML = content;
            }
          }
        });
      }
    }
  }
}

export default new DynamicLoader();
